import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, CarFront, MapPin, ChevronDown } from "lucide-react";
import { IMG, wa } from "../lib/site";

const lines = [
  { text: "Sua experiência em", italic: false },
  { text: "Foz do Iguaçu", italic: false },
  { text: "começa aqui!", italic: true },
];

const badges = [
  { icon: CarFront, text: "Sprinter para até 18 passageiros" },
  { icon: MapPin, text: "Guia de turismo em Foz do Iguaçu" },
  { icon: null, text: "🇧🇷 🇦🇷 🇵🇾 Experiências na Tríplice Fronteira" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="inicio" ref={ref} className="relative min-h-[100svh] flex items-end overflow-hidden grain" data-testid="hero-section">
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <img
          src={IMG.hero}
          alt="Cataratas do Iguaçu, Foz do Iguaçu, Paraná"
          className="w-full h-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-brand-deep/45" />
      <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-brand-deep/85 via-brand-deep/30 to-transparent" />
      <div className="absolute inset-y-0 left-0 w-2/3 bg-gradient-to-r from-brand-deep/50 to-transparent" />

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full pb-24 pt-40">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.8 }}
          className="text-white/80 text-xs font-semibold uppercase tracking-[0.35em] mb-6"
        >
          Guia de turismo · Foz do Iguaçu · Tríplice Fronteira
        </motion.p>

        <h1 className="font-display font-extrabold tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.04] text-balance [text-shadow:0_2px_24px_rgba(2,43,39,0.6)]">
          {lines.map((l, i) => (
            <span className="mask-line" key={l.text}>
              <motion.span
                className={`block ${l.italic ? "font-serif italic font-medium text-accent-soft" : ""}`}
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.3 + i * 0.14, duration: 1, ease: [0.22, 1, 0.36, 1] }}
              >
                {l.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.9 }}
          className="mt-6 max-w-xl text-white/85 text-base md:text-lg leading-relaxed"
        >
          Conheça Foz do Iguaçu, Argentina e Paraguai com um guia que conhece a região há mais de 15 anos.
        </motion.p>

        <motion.ul
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.05, duration: 0.9 }}
          className="mt-7 flex flex-col sm:flex-row sm:flex-wrap gap-3"
          data-testid="hero-trust-indicators"
        >
          {badges.map((b) => (
            <li key={b.text} className="flex w-fit items-center gap-2.5 rounded-full border border-white/25 bg-white/10 backdrop-blur-md px-4 py-2 text-white/90 text-sm">
              {b.icon && <b.icon size={15} className="text-accent-soft" />}
              {b.text}
            </li>
          ))}
        </motion.ul>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.9 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <a
            href={wa("Olá, Magnus! Quero planejar minha viagem para Foz do Iguaçu.")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-cta-planejar"
            className="group inline-flex items-center gap-2 rounded-full bg-accent px-8 py-4 text-sm font-bold text-white shadow-xl shadow-accent/30 transition-transform duration-300 hover:scale-[1.04]"
          >
            Quero planejar minha viagem
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </a>
          <a
            href="/passeios"
            data-testid="hero-cta-passeios"
            className="inline-flex items-center gap-2 rounded-full border border-white/40 bg-white/10 backdrop-blur-md px-8 py-4 text-sm font-bold text-white transition-colors duration-300 hover:bg-white/20"
          >
            Ver passeios
          </a>
        </motion.div>
      </motion.div>

      <motion.a
        href="#passeios"
        onClick={(e) => {
          e.preventDefault();
          window.__lenis?.scrollTo("#passeios", { offset: -130, duration: 1.4 });
        }}
        data-testid="hero-scroll-indicator"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-8 right-8 hidden md:grid place-items-center w-12 h-12 rounded-full border border-white/30 text-white/80 z-10"
      >
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ChevronDown size={18} />
        </motion.span>
      </motion.a>
    </section>
  );
}
